<template>
  <div class="video-custom-wrapper" @contextmenu.prevent>
    <video
      ref="videoEl"
      :src="src"
      preload="metadata"
      playsinline
      webkit-playsinline
      x-webkit-airplay="deny"
      disablePictureInPicture
      controlsList="nodownload nofullscreen noremoteplayback"
      style="width: 100%; display: block; background: #000;"
      @timeupdate="onTimeUpdate"
      @ended="onEnded"
      @pause="onPause"
      @loadedmetadata="onLoaded"
      @waiting="isBuffering = true"
      @canplay="isBuffering = false"
      @webkitbeginfullscreen.prevent="onIOSFullscreenBlock"
    />

    <!-- buffering spinner -->
    <div v-if="isBuffering" class="video-overlay-center">
      <div class="spinner-border text-light" role="status" />
    </div>

    <!-- controls bar -->
    <div class="video-controls" @mouseenter="showControls = true" @mouseleave="startHideTimer">
      <!-- progress bar (seek disabled: show-only) -->
      <div class="video-progress-track" @click.prevent @mousedown.prevent @touchstart.prevent>
        <div class="video-progress-fill" :style="{ width: progressPct + '%' }" />
        <div class="video-progress-thumb" :style="{ left: progressPct + '%' }" />
      </div>

      <div class="video-controls-row">
        <!-- play / pause -->
        <button class="vc-btn" @click="togglePlay">
          <i :class="isPlaying ? 'fa fa-pause' : 'fa fa-play'" />
        </button>

        <!-- time -->
        <span class="vc-time">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>

        <div style="flex:1" />

        <!-- volume -->
        <button class="vc-btn" @click="toggleMute">
          <i :class="isMuted ? 'fa fa-volume-mute' : 'fa fa-volume-up'" />
        </button>
        <input
          class="vc-volume"
          type="range"
          min="0"
          max="1"
          step="0.05"
          :value="volume"
          @input="onVolumeInput"
        />

        <!-- fullscreen (hidden on iOS to prevent native player seek) -->
        <button v-if="!isIOS" class="vc-btn" @click="toggleFullscreen">
          <i class="fa fa-expand" />
        </button>
      </div>
    </div>

    <!-- click-to-play overlay (also blocks right-click seek via context menu) -->
    <div class="video-click-overlay" @click="togglePlay" @contextmenu.prevent />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onUnmounted } from "vue";

export default defineComponent({
  name: "VideoCustom",
  props: {
    src: { type: String, required: true },
    allowSeek: { type: Boolean, default: false },
  },
  emits: ["ended", "pause", "timeupdate"],
  setup(props, { emit, expose }) {
    const videoEl = ref<HTMLVideoElement | null>(null);
    const isPlaying = ref(false);
    const isBuffering = ref(false);
    const isMuted = ref(false);
    const volume = ref(1);
    const currentTime = ref(0);
    const duration = ref(0);
    const maxReached = ref(0);
    const showControls = ref(true);
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let lastProgressEmit = -10;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    const onIOSFullscreenBlock = () => {
      if (videoEl.value) {
        videoEl.value.pause();
        (videoEl.value as any).webkitExitFullscreen?.();
        videoEl.value.play();
      }
    };

    const progressPct = computed(() =>
      duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0
    );

    const formatTime = (s: number) => {
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${m}:${sec.toString().padStart(2, "0")}`;
    };

    const onLoaded = () => {
      if (videoEl.value) duration.value = videoEl.value.duration;
    };

    const onTimeUpdate = () => {
      if (!videoEl.value) return;
      const t = videoEl.value.currentTime;
      if (!props.allowSeek && t > maxReached.value + 1) {
        videoEl.value.currentTime = maxReached.value;
        return;
      }
      if (t > maxReached.value) maxReached.value = t;
      currentTime.value = t;
      if (t - lastProgressEmit >= 10) {
        lastProgressEmit = t;
        emit("timeupdate", t);
      }
    };

    const onEnded = () => {
      isPlaying.value = false;
      emit("ended");
    };

    const onPause = () => {
      isPlaying.value = false;
      emit("pause");
    };

    const startHideTimer = () => {
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        if (isPlaying.value) showControls.value = false;
      }, 3000);
    };

    const togglePlay = () => {
      if (!videoEl.value) return;
      if (videoEl.value.paused) {
        videoEl.value.play();
        isPlaying.value = true;
        startHideTimer();
      } else {
        videoEl.value.pause();
        isPlaying.value = false;
        showControls.value = true;
      }
    };

    const toggleMute = () => {
      if (!videoEl.value) return;
      isMuted.value = !isMuted.value;
      videoEl.value.muted = isMuted.value;
    };

    const onVolumeInput = (e: Event) => {
      const v = parseFloat((e.target as HTMLInputElement).value);
      volume.value = v;
      if (videoEl.value) videoEl.value.volume = v;
      isMuted.value = v === 0;
    };

    const toggleFullscreen = () => {
      const el = videoEl.value?.parentElement;
      if (!el) return;
      if (!document.fullscreenElement) {
        el.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    };

    const pause = () => videoEl.value?.pause();
    const play = () => videoEl.value?.play();
    const load = () => videoEl.value?.load();
    const resetMax = () => { maxReached.value = 0; };
    const seekTo = (time: number) => {
      if (!videoEl.value) return;
      maxReached.value = time;
      videoEl.value.currentTime = time;
    };

    // Expose native-video-like API so parent can use videoRef.value.pause() etc.
    expose({
      get src() { return videoEl.value?.src ?? ""; },
      set src(v: string) { if (videoEl.value) videoEl.value.src = v; },
      get duration() { return videoEl.value?.duration ?? 0; },
      get currentTime() { return videoEl.value?.currentTime ?? 0; },
      set currentTime(v: number) { if (videoEl.value) videoEl.value.currentTime = v; },
      get paused() { return videoEl.value?.paused ?? true; },
      get ended() { return videoEl.value?.ended ?? false; },
      pause,
      play,
      load,
      resetMax,
      seekTo,
      addEventListener: (type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) => {
        videoEl.value?.addEventListener(type, listener, options);
      },
      removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => {
        videoEl.value?.removeEventListener(type, listener);
      },
    });

    onUnmounted(() => {
      if (hideTimer) clearTimeout(hideTimer);
      if (videoEl.value) {
        videoEl.value.pause();
        videoEl.value.src = "";
        videoEl.value.load();
      }
    });

    return {
      videoEl,
      isPlaying,
      isBuffering,
      isMuted,
      volume,
      currentTime,
      duration,
      showControls,
      progressPct,
      formatTime,
      onLoaded,
      onTimeUpdate,
      onEnded,
      onPause,
      togglePlay,
      toggleMute,
      onVolumeInput,
      toggleFullscreen,
      startHideTimer,
      pause,
      resetMax,
      isIOS,
      onIOSFullscreenBlock,
    };
  },
});
</script>

<style scoped>
.video-custom-wrapper {
  position: relative;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  user-select: none;
}

.video-overlay-center {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.video-click-overlay {
  position: absolute;
  inset: 0;
  /* leave bottom 60px for controls */
  bottom: 60px;
  cursor: pointer;
}

.video-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.75));
  padding: 8px 12px 10px;
  transition: opacity 0.3s;
}

.video-progress-track {
  position: relative;
  height: 4px;
  background: rgba(255,255,255,0.3);
  border-radius: 2px;
  margin-bottom: 8px;
  cursor: default;
  pointer-events: none;
}

.video-progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #323563;
  border-radius: 2px;
  pointer-events: none;
}

.video-progress-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  pointer-events: none;
}

.video-controls-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vc-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 16px;
  padding: 4px 6px;
  cursor: pointer;
  line-height: 1;
}

.vc-btn:hover { opacity: 0.8; }

.vc-time {
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
}

.vc-volume {
  width: 70px;
  accent-color: #323563;
  cursor: pointer;
}
</style>
